import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * System Module Service Stores
 * Updated to match backend api-types
 */
export const systemStores: ServiceStoreConfig[] = [
  {
    name: "api-services",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.apiServicesStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalServices = entities.length;
          const operational = entities.filter(
            (service: Record<string, unknown>) => service.status === "operational" || service.status === "healthy"
          ).length;
          const degraded = entities.filter(
            (service: Record<string, unknown>) => service.status === "degraded" || service.status === "warning"
          ).length;
          const down = entities.filter(
            (service: Record<string, unknown>) => service.status === "down" || service.status === "error"
          ).length;

          return [
            {
              title: "Total Services",
              value: () => totalServices.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Operational",
              value: () => operational.toString(),
              valueColor: "success.main",
            },
            {
              title: "Degraded",
              value: () => degraded.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Down",
              value: () => down.toString(),
              valueColor: "error.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "app-version",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.appVersionStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestVersion = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const currentVersion = latestVersion?.version || "N/A";
          const buildNumber = latestVersion?.buildNumber || "N/A";
          const releaseDate = latestVersion?.releaseDate || "N/A";
          const environment = latestVersion?.environment || "N/A";

          return [
            {
              title: "Current Version",
              value: () => (currentVersion as string).toString(),
              valueColor: "primary.main",
            },
            {
              title: "Build Number",
              value: () => (buildNumber as string | number).toString(),
              valueColor: "info.main",
            },
            {
              title: "Environment",
              value: () => (environment as string).toString(),
              valueColor: "success.main",
            },
            {
              title: "Release Date",
              value: () => new Date(releaseDate as string).toLocaleDateString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "azure-blob-storage",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.azureBlobStorageStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalBlobs = entities.length;
          const totalSize = entities.reduce((sum: number, blob: Record<string, unknown>) => {
            return sum + ((blob.sizeBytes as number) || 0);
          }, 0);
          const uniqueContainers = new Set(
            entities.map((blob: Record<string, unknown>) => blob.containerName)
          ).size;
          const thisMonth = entities.filter((blob: Record<string, unknown>) => {
            const uploadDate = new Date(blob.uploadedAt as string);
            const now = new Date();
            return (
              uploadDate.getMonth() === now.getMonth() &&
              uploadDate.getFullYear() === now.getFullYear()
            );
          }).length;

          return [
            {
              title: "Total Blobs",
              value: () => totalBlobs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Size",
              value: () => `${((totalSize as number) / 1024 / 1024 / 1024).toFixed(2)} GB`,
              valueColor: "info.main",
            },
            {
              title: "Containers",
              value: () => uniqueContainers.toString(),
              valueColor: "success.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "dashboard-metrics",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.dashboardMetricsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestMetrics = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const activeUsers = latestMetrics?.activeUsers || 0;
          const totalProjects = latestMetrics?.totalProjects || 0;
          const totalPanels = latestMetrics?.totalPanels || 0;
          const systemUptime = latestMetrics?.systemUptimePercent || 0;

          return [
            {
              title: "Active Users",
              value: () => (activeUsers as number).toString(),
              valueColor: "primary.main",
            },
            {
              title: "Total Projects",
              value: () => (totalProjects as number).toString(),
              valueColor: "info.main",
            },
            {
              title: "Total Panels",
              value: () => (totalPanels as number).toString(),
              valueColor: "success.main",
            },
            {
              title: "System Uptime",
              value: () => `${(systemUptime as number).toFixed(2)}%`,
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "generate-seed-data",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.generateSeedDataStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRuns = entities.length;
          const successful = entities.filter(
            (run: Record<string, unknown>) => run.status === "success" || run.status === "completed"
          ).length;
          const failed = entities.filter(
            (run: Record<string, unknown>) => run.status === "failed" || run.status === "error"
          ).length;
          const lastRunDate = entities.length > 0
            ? new Date((entities[0] as Record<string, unknown>).createdAt as string).toLocaleDateString()
            : "Never";

          return [
            {
              title: "Total Runs",
              value: () => totalRuns.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Successful",
              value: () => successful.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "Last Run",
              value: () => lastRunDate.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "health",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.healthStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestHealth = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const status = latestHealth?.status || "unknown";
          const uptime = latestHealth?.uptimeSeconds || 0;
          const memoryUsage = latestHealth?.memoryUsagePercent || 0;
          const cpuUsage = latestHealth?.cpuUsagePercent || 0;

          return [
            {
              title: "Status",
              value: () => (status as string).toUpperCase(),
              valueColor: status === "healthy" ? "success.main" : "error.main",
            },
            {
              title: "Uptime",
              value: () => `${((uptime as number) / 3600).toFixed(1)}h`,
              valueColor: "primary.main",
            },
            {
              title: "Memory Usage",
              value: () => `${(memoryUsage as number).toFixed(1)}%`,
              valueColor: "warning.main",
            },
            {
              title: "CPU Usage",
              value: () => `${(cpuUsage as number).toFixed(1)}%`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "kpi-targets",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.kpiTargetsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalTargets = entities.length;
          const achieved = entities.filter(
            (target: Record<string, unknown>) => target.status === "achieved" || target.status === "met"
          ).length;
          const atRisk = entities.filter(
            (target: Record<string, unknown>) => target.status === "at_risk" || target.status === "warning"
          ).length;
          const avgCompletion = entities.length > 0
            ? entities.reduce((sum: number, target: Record<string, unknown>) => {
                return sum + ((target.completionPercent as number) || 0);
              }, 0) / entities.length
            : 0;

          return [
            {
              title: "Total Targets",
              value: () => totalTargets.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Achieved",
              value: () => achieved.toString(),
              valueColor: "success.main",
            },
            {
              title: "At Risk",
              value: () => atRisk.toString(),
              valueColor: "error.main",
            },
            {
              title: "Avg Completion",
              value: () => `${(avgCompletion as number).toFixed(1)}%`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "log",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.logStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalLogs = entities.length;
          const errors = entities.filter(
            (log: Record<string, unknown>) => log.level === "error"
          ).length;
          const warnings = entities.filter(
            (log: Record<string, unknown>) => log.level === "warn" || log.level === "warning"
          ).length;
          const info = entities.filter(
            (log: Record<string, unknown>) => log.level === "info"
          ).length;

          return [
            {
              title: "Total Logs",
              value: () => totalLogs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Errors",
              value: () => errors.toString(),
              valueColor: "error.main",
            },
            {
              title: "Warnings",
              value: () => warnings.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Info",
              value: () => info.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "removed-items",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.removedItemsStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalRemoved = entities.length;
          const thisWeek = entities.filter((item: Record<string, unknown>) => {
            const removedDate = new Date(item.removedAt as string);
            const now = new Date();
            const weekAgo = new Date();
            weekAgo.setDate(now.getDate() - 7);
            return removedDate > weekAgo;
          }).length;
          const uniqueTypes = new Set(
            entities.map((item: Record<string, unknown>) => item.entityType)
          ).size;
          const canRecover = entities.filter(
            (item: Record<string, unknown>) => item.canRecover === true
          ).length;

          return [
            {
              title: "Total Removed",
              value: () => totalRemoved.toString(),
              valueColor: "primary.main",
            },
            {
              title: "This Week",
              value: () => thisWeek.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Entity Types",
              value: () => uniqueTypes.toString(),
              valueColor: "info.main",
            },
            {
              title: "Recoverable",
              value: () => canRecover.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "safety-summary",
    options: {
      enableStorage: false,
      enableErrorNotifications: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.safetySummaryStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestSummary = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const incidentCount = latestSummary?.incidentCount || 0;
          const daysWithoutIncident = latestSummary?.daysWithoutIncident || 0;
          const safetyTrainingCompliance = latestSummary?.safetyTrainingCompliancePercent || 0;
          const inspectionsPassed = latestSummary?.inspectionsPassedPercent || 0;

          return [
            {
              title: "Total Incidents",
              value: () => (incidentCount as number).toString(),
              valueColor: incidentCount > 0 ? "error.main" : "success.main",
            },
            {
              title: "Days Without Incident",
              value: () => (daysWithoutIncident as number).toString(),
              valueColor: "success.main",
            },
            {
              title: "Training Compliance",
              value: () => `${(safetyTrainingCompliance as number).toFixed(1)}%`,
              valueColor: "primary.main",
            },
            {
              title: "Inspections Passed",
              value: () => `${(inspectionsPassed as number).toFixed(1)}%`,
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "update-history",
    options: {
      enableStorage: true,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.updateHistoryStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalUpdates = entities.length;
          const successful = entities.filter(
            (update: Record<string, unknown>) => update.status === "success" || update.status === "completed"
          ).length;
          const failed = entities.filter(
            (update: Record<string, unknown>) => update.status === "failed" || update.status === "error"
          ).length;
          const thisMonth = entities.filter((update: Record<string, unknown>) => {
            const updateDate = new Date(update.updatedAt as string);
            const now = new Date();
            return (
              updateDate.getMonth() === now.getMonth() &&
              updateDate.getFullYear() === now.getFullYear()
            );
          }).length;

          return [
            {
              title: "Total Updates",
              value: () => totalUpdates.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Successful",
              value: () => successful.toString(),
              valueColor: "success.main",
            },
            {
              title: "Failed",
              value: () => failed.toString(),
              valueColor: "error.main",
            },
            {
              title: "This Month",
              value: () => thisMonth.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
    },
  },
  {
    name: "weather",
    options: {
      enableStorage: false,
      enableErrorNotifications: false,
      // No table in this tenant (backend audit 2026-08-10): the service is not
      // configured server-side, so the boot find() 404s on every refresh and
      // login. Do not register it server-side to fix this — that turns a 404
      // into a 500, which is louder.
      disableInitialFind: true,
      serviceView: {
        enableServiceView: true,
        actions: {
          enableInlineEdit: true,
          enableRemove: true,
        },
        summaryCards: (rootStore) => {
          const store = rootStore.weatherStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const latestWeather = entities.length > 0 ? (entities[0] as Record<string, unknown>) : null;
          const temperature = latestWeather?.temperatureF || "N/A";
          const conditions = latestWeather?.conditions || "N/A";
          const humidity = latestWeather?.humidityPercent || "N/A";
          const windSpeed = latestWeather?.windSpeedMph || "N/A";

          return [
            {
              title: "Temperature",
              value: () => `${(temperature as string | number).toString()}°F`,
              valueColor: "primary.main",
            },
            {
              title: "Conditions",
              value: () => (conditions as string).toString(),
              valueColor: "info.main",
            },
            {
              title: "Humidity",
              value: () => `${(humidity as string | number).toString()}%`,
              valueColor: "success.main",
            },
            {
              title: "Wind Speed",
              value: () => `${(windSpeed as string | number).toString()} mph`,
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];
